import * as Sentry from '@sentry/react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { queryKeys, useGetCollectionQuery } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { buildDynamicBreadcrumbs } from '@vizit/utils';
import { CollectionDetailsPage, PagesProvider } from '../../../pages';
import { LIBRARY_BREADCRUMB, beforeLoadAuthenticatedValidation } from '../../../router/route-utils';
import { BreadcrumbsPageTemplate } from '../../../templates';

export const Route = createFileRoute('/_authenticated/library/collections/$collectionId')({
  staticData: {
    pageName: 'Collection Details',
  },
  beforeLoad: ({ context, location }) => beforeLoadAuthenticatedValidation(context, location),
  component: RouteComponent,
  loader: async ({ location, params, context }) => {
    const portfolioId = context.auth?.defaultPortfolioId;

    let collectionName = 'Collection Details';

    if (portfolioId) {
      try {
        const collectionData = await context.queryClient.ensureQueryData({
          queryKey: queryKeys.collections.detail({
            portfolio_id: portfolioId,
            collection_id: params.collectionId,
          }).queryKey,
          queryFn: () =>
            useGetCollectionQuery.fetcher({
              portfolio_id: portfolioId,
              collection_id: params.collectionId,
            })(),
        });

        collectionName = collectionData?.name ?? 'Collection Details';
      } catch (error) {
        Sentry.captureException(error, {
          level: 'warning',
          tags: {
            feature: SENTRY_FEATURE_TAGS.Navigation,
            stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Navigation].breadcrumbs,
          },
          extra: {
            page: 'collection details',
          },
        });
      }
    }

    const breadcrumbs = buildDynamicBreadcrumbs(
      location.state?.from,
      collectionName,
      // Current page is not clickable, so we don't need to pass href
      undefined,
      location.search,
      LIBRARY_BREADCRUMB,
    );

    return { breadcrumbs };
  },
});

function RouteComponent() {
  const { collectionId } = useParams({ from: Route.id });
  const { auth } = Route.useRouteContext();

  return (
    <PagesProvider template={BreadcrumbsPageTemplate} templateProps={{ breadcrumbActionsPosition: 'inline' }}>
      <CollectionDetailsPage
        collectionId={collectionId}
        portfolioId={auth?.defaultPortfolioId ?? undefined}
        organizationId={auth?.organizationId ?? undefined}
      />
    </PagesProvider>
  );
}
